<template>
  <v-container style="padding-bottom:0px">
    <v-expansion-panels v-model="panelExpanded" class="mt-4">
      <v-expansion-panel id="raw-call-diagnostics-panel">

        <v-expansion-panel-title>Raw Call Diagnostics</v-expansion-panel-title>

        <v-expansion-panel-text>

          <!-- Stats Bar -->
          <div class="stats-bar">
            <div class="stat-item">
              <span class="stat-value">{{ stats.total }}</span>
              <span class="stat-label">Total Calls</span>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-item">
              <span class="stat-value">{{ stats.pctBail }}%</span>
              <span class="stat-label">Bail</span>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-item">
              <span class="stat-value">{{ stats.pctJail }}%</span>
              <span class="stat-label">Jail</span>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-item">
              <span class="stat-value">{{ stats.pctHangup }}%</span>
              <span class="stat-label">Hangup</span>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-item">
              <span class="stat-value">{{ stats.pctError }}%</span>
              <span class="stat-label">Error</span>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-item">
              <span class="stat-value">{{ stats.pctAcknowledged }}%</span>
              <span class="stat-label">Acknowledged (of Jail)</span>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-item" :class="{ 'stat-alert': stats.pctMaxRetries > 0 }">
              <span class="stat-value">{{ stats.pctMaxRetries }}%</span>
              <span class="stat-label">Hit Max Retries (of Jail)</span>
            </div>
          </div>

          <!-- Filter Controls -->
          <div class="filter-bar">

            <div class="filter-group">
              <label class="filter-label">From</label>
              <input type="date" v-model="filters.dateFrom" class="filter-input" />
            </div>

            <div class="filter-group">
              <label class="filter-label">To</label>
              <input type="date" v-model="filters.dateTo" class="filter-input" />
            </div>

            <div class="filter-group">
              <label class="filter-label">Call Type</label>
              <select v-model="filters.callType" class="filter-select">
                <option value="">All</option>
                <option value="JAIL">JAIL</option>
                <option value="BAIL">BAIL</option>
                <option value="JAIL_FREE">JAIL_FREE</option>
                <option value="JAIL_UNRESOLVED">JAIL_UNRESOLVED</option>
                <option value="UNKNOWN">HANGUP</option>
              </select>
            </div>

            <div class="filter-group">
              <label class="filter-label">Outcome</label>
              <select v-model="filters.outcome" class="filter-select">
                <option value="">All</option>
                <option value="MENU_SUBSYSTEM">MENU</option>
                <option value="VOICEMAIL_SUBSYSTEM">VOICEMAIL</option>
                <option value="FORWARD">FORWARD</option>
                <option value="FAILED">FAILED</option>
              </select>
            </div>

            <div class="filter-group filter-group-checkbox">
              <label class="filter-checkbox-label">
                <input type="checkbox" v-model="filters.maxRetriesOnly" class="filter-checkbox" />
                Max Retries Only
              </label>
            </div>

            <div class="filter-group">
              <label class="filter-label">Caller #</label>
              <input
                type="text"
                v-model="filters.callerId"
                class="filter-input filter-input-caller"
                placeholder="e.g. +12065551234"
              />
            </div>

            <button class="fetch-button" @click="fetchRecords" :disabled="isLoading">
              {{ isLoading ? 'Loading...' : 'Fetch' }}
            </button>

          </div>

          <!-- Record count -->
          <div class="record-count" v-if="!isLoading">
            {{ filteredRecords.length }} record{{ filteredRecords.length !== 1 ? 's' : '' }}
          </div>

          <!-- Records list -->
          <div class="diagnostics-container">
            <div v-if="isLoading" class="loading-msg">Loading...</div>
            <div v-else-if="filteredRecords.length === 0" class="empty-msg">
              No records found for the selected filters.
            </div>
            <raw-call-diagnostics-record
              v-for="record in filteredRecords"
              :key="record.callSid"
              :record="record"
            />
          </div>

        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
  </v-container>
</template>


<script>
import RawCallDiagnosticsRecord from '@/components/CompanyAccount-RawCallDiagnosticsRecord.vue';
import moment from 'moment-timezone';
import axios from 'axios';
import { mapGetters } from 'vuex';

export default {
  name: 'CompanyAccountRawCallDiagnostics',

  components: {
    RawCallDiagnosticsRecord
  },

  data() {
    const today    = moment().format('YYYY-MM-DD');
    const sevenAgo = moment().subtract(7, 'days').format('YYYY-MM-DD');

    return {
      panelExpanded: null,
      hasFetchedOnce: false,
      isLoading: false,
      records: [],
      filters: {
        dateFrom:       sevenAgo,
        dateTo:         today,
        callType:       '',
        outcome:        '',
        maxRetriesOnly: false,
        callerId:       ''
      }
    };
  },

  computed: {
    ...mapGetters(['getSelectedCompany']),

    filteredRecords() {
      return this.records.filter(r => {
        if (this.filters.callType && r.callType !== this.filters.callType) return false;
        if (this.filters.outcome  && r.outcome  !== this.filters.outcome)  return false;
        if (this.filters.maxRetriesOnly && r.dtmfOutcome !== 'MAX_RETRIES_EXCEEDED') return false;
        if (this.filters.callerId && !r.callerId.includes(this.filters.callerId)) return false;
        return true;
      });
    },

    stats() {
      const records = this.filteredRecords;
      const total   = records.length;

      if (total === 0) {
        return { total: 0, pctBail: 0, pctJail: 0, pctError: 0, pctHangup: 0, pctAcknowledged: 0, pctMaxRetries: 0 };
      }

      const jailTypes = ['JAIL', 'JAIL_FREE'];
      const allJail   = ['JAIL', 'JAIL_FREE', 'JAIL_UNRESOLVED'];

      const bailCount   = records.filter(r => r.callType === 'BAIL').length;
      const jailCount   = records.filter(r => jailTypes.includes(r.callType)).length;
      const errorCount  = records.filter(r => r.callType === 'JAIL_UNRESOLVED').length;
      const hangupCount = records.filter(r => r.callType === 'UNKNOWN').length;

      const jailDenominator   = records.filter(r => allJail.includes(r.callType)).length;
      const acknowledgedCount = records.filter(r => r.dtmfOutcome === 'ACKNOWLEDGED').length;
      const maxRetriesCount   = records.filter(r => r.dtmfOutcome === 'MAX_RETRIES_EXCEEDED').length;

      const pctAcknowledged = jailDenominator > 0
        ? Math.round((acknowledgedCount / jailDenominator) * 100)
        : 0;
      const pctMaxRetries = jailDenominator > 0
        ? Math.round((maxRetriesCount / jailDenominator) * 100)
        : 0;

      return {
        total,
        pctBail:         Math.round((bailCount   / total) * 100),
        pctJail:         Math.round((jailCount   / total) * 100),
        pctError:        Math.round((errorCount  / total) * 100),
        pctHangup:       Math.round((hangupCount / total) * 100),
        pctAcknowledged,
        pctMaxRetries
      };
    }
  },

  watch: {
    panelExpanded(newValue) {
      if (newValue === 0 && !this.hasFetchedOnce) {
        this.fetchRecords();
      }
    }
  },

  methods: {
    async fetchRecords() {
      this.isLoading = true;
      try {
        const companyNumber = this.getSelectedCompany?.companyNumber;
        if (!companyNumber) return;

        const params = {
          limit: 200,
          dateFrom: this.filters.dateFrom,
          dateTo:   this.filters.dateTo,
          timezone: this.getSelectedCompany.timezone
        };

        if (this.filters.callType)      params.callType    = this.filters.callType;
        if (this.filters.outcome)       params.outcome     = this.filters.outcome;
        if (this.filters.maxRetriesOnly) params.dtmfOutcome = 'MAX_RETRIES_EXCEEDED';
        if (this.filters.callerId)      params.callerId    = this.filters.callerId;

        const response = await axios.get(
          `/portaldb/db/rawCallDiagnostics/${companyNumber}`,
          { params }
        );

        this.records = response.data;
        this.hasFetchedOnce = true;

      } catch (error) {
        console.error('Error fetching raw call diagnostics:', error);
      } finally {
        this.isLoading = false;
      }
    }
  }
};
</script>


<style scoped>

/* ── Stats bar ──────────────────────────────────────────────────── */
.stats-bar {
  display: flex;
  align-items: center;
  background-color: #f5f5dc;
  border: 1px solid #d2b48c;
  border-radius: 4px;
  padding: 10px 20px;
  margin-bottom: 14px;
  flex-wrap: wrap;
  gap: 8px;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 16px;
}

.stat-value {
  font-size: 22px;
  font-weight: 700;
  color: #5d3a1a;
  line-height: 1.1;
}

.stat-label {
  font-size: 11px;
  color: #8b6914;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 2px;
}

.stat-alert .stat-value {
  color: #c0392b;
}

.stat-alert .stat-label {
  color: #c0392b;
}

.stat-divider {
  width: 1px;
  height: 36px;
  background-color: #d2b48c;
  flex-shrink: 0;
}

/* ── Filter bar ─────────────────────────────────────────────────── */
.filter-bar {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 12px;
  background-color: #fafaf0;
  border: 1px solid #d2b48c;
  border-radius: 4px;
  padding: 12px 14px;
  margin-bottom: 12px;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.filter-group-checkbox {
  justify-content: flex-end;
  padding-bottom: 4px;
}

.filter-label {
  font-size: 11px;
  font-weight: 600;
  color: #8b4513;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.filter-input,
.filter-select {
  border: 1px solid #d2b48c;
  border-radius: 3px;
  padding: 5px 8px;
  font-size: 13px;
  background-color: white;
  color: #333;
  height: 32px;
}

.filter-input:focus,
.filter-select:focus {
  outline: none;
  border-color: #8b4513;
}

.filter-input-caller {
  width: 148px;
}

.filter-checkbox-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #5d3a1a;
  cursor: pointer;
  white-space: nowrap;
}

.filter-checkbox {
  accent-color: #8b4513;
  width: 15px;
  height: 15px;
}

.fetch-button {
  height: 32px;
  padding: 0 20px;
  background-color: #d2b48c;
  border: 1px solid #b8956a;
  border-radius: 3px;
  color: #5d3a1a;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s;
  align-self: flex-end;
}

.fetch-button:hover:not(:disabled) {
  background-color: #c8a472;
}

.fetch-button:disabled {
  opacity: 0.6;
  cursor: default;
}

/* ── Record count ───────────────────────────────────────────────── */
.record-count {
  font-size: 12px;
  color: #888;
  margin-bottom: 8px;
  padding-left: 2px;
}

/* ── Records container ──────────────────────────────────────────── */
.diagnostics-container {
  max-height: 700px;
  overflow-y: auto;
}

.loading-msg,
.empty-msg {
  color: #999;
  font-style: italic;
  font-size: 14px;
  padding: 20px;
  text-align: center;
}

/* ── Expansion panel theming (matches CompanyAccount.vue) ───────── */
:deep(.v-expansion-panels > .v-expansion-panel > .v-expansion-panel-title) {
  font-size: 1.25rem !important;
  font-weight: 600 !important;
  color: #ffffff !important;
  background-color: #d2b48c !important;
  border: 1px solid #d2b48c !important;
  min-height: 64px !important;
  padding: 16px !important;
}

:deep(.v-expansion-panels > .v-expansion-panel > .v-expansion-panel-title .v-expansion-panel-title__icon .v-icon) {
  color: #ffffff !important;
}

:deep(.v-expansion-panel-text) {
  background-color: #f5f5dc !important;
  border: 1px solid #d2b48c !important;
  border-top: none !important;
}

#raw-call-diagnostics-panel {
  margin-bottom: 8px;
}

/* ── Responsive ─────────────────────────────────────────────────── */
@media (max-width: 600px) {
  .stats-bar {
    justify-content: center;
  }

  .filter-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-input,
  .filter-select,
  .fetch-button {
    width: 100%;
  }

  .filter-input-caller {
    width: 100%;
  }
}
</style>
